import * as THREE from 'three';
import type { NodeImplementation } from '@spotar/runtime';
import { runMoveToImplementation } from './node-implementations/move-to-implementation';
import { runPlayAnimationImplementation } from './node-implementations/play-animation-implementation';
import { runRotateToImplementation } from './node-implementations/rotate-to-implementation';

const CM_TO_M = 0.01;

/** Steps below these tolerances are skipped so the character does not flash through a near-zero walk or turn. */
const POSITION_THRESHOLD_CM = 0.1;
const ROTATION_THRESHOLD_DEG = 0.1;

export interface CharacterData {
  target: { id: string };
  walkingAnimation?: string;
  rotateAnimationRight?: string;
  rotateAnimationLeft?: string;
  idleAnimation?: string;
  moveSpeed?: number;
  rotateSpeed?: number;
  curve?: string;
}

export interface Vector3Deg {
  x: number;
  y: number;
  z: number;
}

function rotationDegreesToEuler(rotation: Vector3Deg, order: THREE.EulerOrder): THREE.Euler {
  return new THREE.Euler(
    THREE.MathUtils.degToRad(rotation.x),
    THREE.MathUtils.degToRad(rotation.y),
    THREE.MathUtils.degToRad(rotation.z),
    order
  );
}

export class Character {
  readonly object: THREE.Object3D;
  readonly target: { id: string };
  readonly walkingAnimation?: string;
  readonly rotateAnimationRight?: string;
  readonly rotateAnimationLeft?: string;
  readonly idleAnimation?: string;
  readonly moveSpeed: number;
  readonly rotateSpeed: number;
  readonly curve?: string;
  readonly nodeId: string;

  constructor(data: CharacterData, object: THREE.Object3D, nodeId: string) {
    this.object = object;
    this.target = data.target;
    this.walkingAnimation = data.walkingAnimation;
    this.rotateAnimationRight = data.rotateAnimationRight;
    this.rotateAnimationLeft = data.rotateAnimationLeft;
    this.idleAnimation = data.idleAnimation;
    this.moveSpeed = data.moveSpeed ?? 100;
    this.rotateSpeed = data.rotateSpeed ?? 90;
    this.curve = data.curve;
    this.nodeId = nodeId;
  }

  getFacingDirection(to: THREE.Vector3): Vector3Deg | null {
    const from = this.object.position;
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    if (dx * dx + dz * dz < 1e-12) return null;

    const yawDeg = THREE.MathUtils.radToDeg(Math.atan2(dx, dz));
    return { x: 0, y: yawDeg, z: 0 };
  }

  isAtPosition(position: THREE.Vector3): boolean {
    return this.object.position.distanceTo(position) <= POSITION_THRESHOLD_CM * CM_TO_M;
  }

  isAtRotation(rotation: Vector3Deg): boolean {
    const targetEuler = rotationDegreesToEuler(rotation, this.object.rotation.order);
    const endQuat = new THREE.Quaternion().setFromEuler(targetEuler);
    const angleDeg = THREE.MathUtils.radToDeg(this.object.quaternion.angleTo(endQuat));
    return angleDeg <= ROTATION_THRESHOLD_DEG;
  }

  getRotateTurnDirection(rotation: Vector3Deg): 'left' | 'right' | null {
    const targetEuler = rotationDegreesToEuler(rotation, this.object.rotation.order);
    const startQuat = this.object.quaternion.clone();
    const endQuat = new THREE.Quaternion().setFromEuler(targetEuler);

    if (startQuat.angleTo(endQuat) < 1e-6) return null;

    const relative = startQuat.clone().invert().multiply(endQuat);
    if (relative.w < 0) {
      relative.x = -relative.x;
      relative.y = -relative.y;
      relative.z = -relative.z;
      relative.w = -relative.w;
    }

    return relative.y >= 0 ? 'left' : 'right';
  }

  async rotateTo(nextRotation: Vector3Deg): Promise<boolean> {
    if (this.isAtRotation(nextRotation)) return false;

    const turnDirection = this.getRotateTurnDirection(nextRotation);
    const rotateAnimation =
      turnDirection === 'left'
        ? this.rotateAnimationLeft
        : turnDirection === 'right'
          ? this.rotateAnimationRight
          : undefined;
    if (rotateAnimation) {
      await runPlayAnimationImplementation(
        {
          target: this.target,
          clip: rotateAnimation,
          loop: true,
          speed: 1,
          parallel: false,
        },
        this.nodeId
      );
    }

    await (runRotateToImplementation as NodeImplementation)(
      {
        target: this.target,
        rotation: nextRotation,
        speed: this.rotateSpeed,
        curve: this.curve,
      },
      this.nodeId
    );

    return true;
  }

  async walkTo(position: THREE.Vector3): Promise<boolean> {
    if (this.isAtPosition(position)) return false;

    if (this.walkingAnimation) {
      await runPlayAnimationImplementation(
        {
          target: this.target,
          clip: this.walkingAnimation,
          loop: true,
          speed: 1,
          parallel: false,
        },
        this.nodeId
      );
    }

    await (runMoveToImplementation as NodeImplementation)(
      {
        target: this.target,
        position,
        speed: this.moveSpeed,
        curve: this.curve,
      },
      this.nodeId
    );

    return true;
  }

  async moveTo(position: THREE.Vector3, rotation: Vector3Deg): Promise<void> {
    let didAnimate = false;

    if (!this.isAtPosition(position)) {
      const facingDirection = this.getFacingDirection(position);
      if (facingDirection && (await this.rotateTo(facingDirection))) didAnimate = true;
      if (await this.walkTo(position)) didAnimate = true;
    }

    if (await this.rotateTo(rotation)) didAnimate = true;

    if (didAnimate && this.idleAnimation) {
      await runPlayAnimationImplementation(
        {
          target: this.target,
          clip: this.idleAnimation,
          loop: true,
          speed: 1,
          parallel: false,
        },
        this.nodeId
      );
    }
  }
}
